import { useEffect, useRef, useState } from "react";
import { Navbar } from "@/components/Navbar";
import { ItemThumb } from "@/components/ItemThumb";
import api, { formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { Plus, Trash2, Loader2, PenLine, X } from "lucide-react";

const EMPTY = "https://images.unsplash.com/photo-1716347685367-1eb5de72eb65?crop=entropy&cs=srgb&fm=jpg&q=85&w=940";
const CATEGORIES = ["all", "top", "bottom", "dress", "outerwear", "shoes", "accessory"];

export default function Wardrobe() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef();
  const [showManual, setShowManual] = useState(false);
  const [saving, setSaving] = useState(false);
  const emptyForm = { name: "", category: "top", primary_color: "", style: "", colors: "", occasions: "" };
  const [form, setForm] = useState(emptyForm);

  const submitManual = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) return;
    setSaving(true);
    try {
      const payload = {
        name: form.name.trim(),
        category: form.category,
        primary_color: form.primary_color.trim() || "neutral",
        colors: form.colors.split(",").map((s) => s.trim()).filter(Boolean),
        style: form.style.trim() || "classic",
        occasions: form.occasions.split(",").map((s) => s.trim().toLowerCase()).filter(Boolean),
      };
      const { data } = await api.post("/clothing/manual", payload);
      setItems((prev) => [data, ...prev]);
      toast.success(`Added "${data.name}"`);
      setShowManual(false);
      setForm(emptyForm);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not add");
    } finally {
      setSaving(false);
    }
  };

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/clothing");
      setItems(data);
    } catch (e) {
      toast.error("Failed to load wardrobe");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    const form = new FormData();
    form.append("file", file);
    try {
      const { data } = await api.post("/clothing", form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setItems((prev) => [data, ...prev]);
      toast.success(`Added "${data.name}"`);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Upload failed");
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const remove = async (id) => {
    try {
      await api.delete(`/clothing/${id}`);
      setItems((prev) => prev.filter((i) => i.id !== id));
      toast.success("Removed");
    } catch (e) {
      toast.error("Failed to remove");
    }
  };

  const shown = filter === "all" ? items : items.filter((i) => i.category === filter);

  return (
    <div className="bg-bg0 min-h-screen">
      <Navbar />
      <input
        ref={fileRef}
        type="file"
        accept="image/png,image/jpeg,image/webp"
        className="hidden"
        onChange={onFile}
        data-testid="upload-file-input"
      />

      <section className="max-w-[1500px] mx-auto px-6 md:px-10 py-12">
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-10">
          <div>
            <div className="overline mb-3 text-volt">Your Closet · {items.length} pieces</div>
            <h1 className="font-display uppercase text-6xl md:text-7xl font-bold tracking-tighter">Wardrobe</h1>
          </div>
          <div className="flex flex-wrap gap-3">
            <button
              onClick={() => setShowManual(true)}
              data-testid="add-manual-btn"
              className="ep-btn inline-flex items-center gap-2 border border-white/25 px-6 py-3.5 text-sm font-bold uppercase tracking-widest hover:bg-white hover:text-black"
            >
              <PenLine size={16} /> Add by Description
            </button>
            <button
              onClick={() => fileRef.current?.click()}
              disabled={uploading}
              data-testid="upload-clothing-btn"
              className="ep-btn inline-flex items-center gap-2 bg-volt text-black px-6 py-3.5 text-sm font-bold uppercase tracking-widest hover:bg-volt-hover disabled:opacity-60"
            >
              {uploading ? (
                <>
                  <Loader2 size={16} className="animate-spin" /> Analysing…
                </>
              ) : (
                <>
                  <Plus size={16} strokeWidth={2.5} /> Upload Photo
                </>
              )}
            </button>
          </div>
        </div>

        <div className="flex flex-wrap gap-6 border-b border-epborder pb-4 mb-10" data-testid="category-filters">
          {CATEGORIES.map((c) => (
            <button
              key={c}
              onClick={() => setFilter(c)}
              data-testid={`filter-${c}`}
              className={`overline ep-btn ${filter === c ? "text-volt" : "text-epmuted hover:text-white"}`}
            >
              {c}
            </button>
          ))}
        </div>

        {loading ? (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-5">
            {[...Array(4)].map((_, i) => (
              <div key={i} className="aspect-[3/4] ep-loading" />
            ))}
          </div>
        ) : shown.length === 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-12 gap-8 items-center border border-epborder" data-testid="wardrobe-empty">
            <div className="md:col-span-5 relative">
              <img src={EMPTY} alt="Empty" className="w-full h-[380px] object-cover" />
              <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent" />
            </div>
            <div className="md:col-span-6 md:col-start-7 p-8 md:p-4">
              <div className="overline mb-4 text-volt">Rack is empty</div>
              <h2 className="font-display uppercase text-4xl font-bold tracking-tight mb-4">
                No fits yet.
              </h2>
              <p className="text-epsub mb-8 leading-relaxed">
                Drop photos of your tops, bottoms, kicks and more. Eternal reads each piece
                and gets ready to pair.
              </p>
              <button
                onClick={() => fileRef.current?.click()}
                data-testid="empty-upload-btn"
                className="ep-btn inline-flex items-center gap-2 bg-volt text-black px-6 py-3 text-sm font-bold uppercase tracking-widest hover:bg-volt-hover"
              >
                <Plus size={16} strokeWidth={2.5} /> Upload first item
              </button>
            </div>
          </div>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-5" data-testid="wardrobe-grid">
            {shown.map((item) => (
              <div key={item.id} className="ep-card group" data-testid={`clothing-card-${item.id}`}>
                <div className="relative overflow-hidden">
                  <ItemThumb
                    item={item}
                    className="w-full aspect-[3/4] object-cover group-hover:scale-105 transition-transform duration-300"
                    testId={`clothing-img-${item.id}`}
                  />
                  <button
                    onClick={() => remove(item.id)}
                    data-testid={`delete-${item.id}`}
                    className="ep-btn absolute top-3 right-3 bg-black/70 backdrop-blur-sm p-2 opacity-0 group-hover:opacity-100 hover:bg-epred border border-white/20"
                  >
                    <Trash2 size={14} strokeWidth={1.75} />
                  </button>
                  <span className="absolute top-3 left-3 overline text-white bg-black/60 px-2 py-1">
                    {item.category}
                  </span>
                </div>
                <div className="p-4">
                  <div className="flex items-center justify-between gap-2 mb-1">
                    <h3 className="font-display uppercase text-lg leading-tight tracking-tight truncate">{item.name}</h3>
                    <span className="w-3.5 h-3.5 border border-white/40 shrink-0" title={item.primary_color} style={{ backgroundColor: cssColor(item.primary_color) }} />
                  </div>
                  <p className="text-xs text-epmuted capitalize font-mono-accent">{item.style}</p>
                </div>
              </div>
            ))}
          </div>
        )}
      {showManual && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center p-4" data-testid="manual-modal">
          <div className="absolute inset-0 bg-black/80 backdrop-blur-sm" onClick={() => setShowManual(false)} />
          <form onSubmit={submitManual} className="relative ep-card w-full max-w-lg p-8 max-h-[90vh] overflow-y-auto">
            <button type="button" onClick={() => setShowManual(false)} data-testid="manual-close" className="absolute top-4 right-4 text-epmuted hover:text-white ep-btn">
              <X size={18} />
            </button>
            <div className="overline text-volt mb-2">Add by description</div>
            <h2 className="font-display uppercase text-3xl font-bold tracking-tight mb-6">Describe your item</h2>
            <p className="text-epsub text-sm -mt-4 mb-6">No photo needed — type it and Eternal styles with it.</p>

            <label className="overline block mb-2">Item name *</label>
            <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required data-testid="manual-name"
              placeholder="e.g. Black bomber jacket"
              className="w-full bg-transparent border-b-2 border-epborder focus:border-volt px-0 py-2.5 mb-5 text-white placeholder:text-zinc-600 transition-colors" />

            <label className="overline block mb-2">Category</label>
            <select value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} data-testid="manual-category"
              className="w-full bg-bg1 border border-epborder focus:border-volt px-3 py-2.5 mb-5 text-white transition-colors">
              {["top", "bottom", "dress", "outerwear", "shoes", "accessory", "other"].map((c) => (
                <option key={c} value={c} className="bg-bg1">{c}</option>
              ))}
            </select>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="overline block mb-2">Primary color</label>
                <input value={form.primary_color} onChange={(e) => setForm({ ...form, primary_color: e.target.value })} data-testid="manual-color"
                  placeholder="black"
                  className="w-full bg-transparent border-b-2 border-epborder focus:border-volt px-0 py-2.5 mb-5 text-white placeholder:text-zinc-600 transition-colors" />
              </div>
              <div>
                <label className="overline block mb-2">Style</label>
                <input value={form.style} onChange={(e) => setForm({ ...form, style: e.target.value })} data-testid="manual-style"
                  placeholder="streetwear"
                  className="w-full bg-transparent border-b-2 border-epborder focus:border-volt px-0 py-2.5 mb-5 text-white placeholder:text-zinc-600 transition-colors" />
              </div>
            </div>

            <label className="overline block mb-2">Other colors (comma separated)</label>
            <input value={form.colors} onChange={(e) => setForm({ ...form, colors: e.target.value })} data-testid="manual-colors"
              placeholder="white, grey"
              className="w-full bg-transparent border-b-2 border-epborder focus:border-volt px-0 py-2.5 mb-5 text-white placeholder:text-zinc-600 transition-colors" />

            <label className="overline block mb-2">Occasions (comma separated)</label>
            <input value={form.occasions} onChange={(e) => setForm({ ...form, occasions: e.target.value })} data-testid="manual-occasions"
              placeholder="casual, party"
              className="w-full bg-transparent border-b-2 border-epborder focus:border-volt px-0 py-2.5 mb-7 text-white placeholder:text-zinc-600 transition-colors" />

            <button type="submit" disabled={saving} data-testid="manual-submit"
              className="ep-btn w-full bg-volt text-black py-3.5 text-sm font-bold uppercase tracking-widest hover:bg-volt-hover disabled:opacity-60">
              {saving ? "Adding…" : "Add Item"}
            </button>
          </form>
        </div>
      )}
      </section>
    </div>
  );
}

function cssColor(name) {
  if (!name) return "#3f3f46";
  const map = {
    neutral: "#a1a1aa", beige: "#e8dcc4", cream: "#f5efe0", navy: "#1e2a4a",
    denim: "#3b5a80", khaki: "#b3a06b", tan: "#c9a87c", charcoal: "#333",
    white: "#f4f4f5", black: "#18181b", blue: "#2563eb",
  };
  return map[name.toLowerCase()] || name.toLowerCase();
}
